import { MerchantPitch } from './marketplace/MerchantPitch';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Store, CheckCircle2, AlertCircle, Clock, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { ImageWithFallback } from './ui/image-with-fallback';
import { CircularImageCropModal } from './CircularImageCropModal';

const initialForm = {
  business_name: '',
  business_type: '',
  industry_id: '',
  business_address: '',
  business_description: '',
  business_logo_url: '',
};

export function MerchantApplyPage() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [industries, setIndustries] = useState([]);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const logoInputRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5 MB.');
      return;
    }
    // Open the circular crop modal — actual upload happens after the user
    // confirms their crop selection.
    setPendingLogoFile(file);
    setCropOpen(true);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleCropConfirm = async (croppedBlob) => {
    if (!croppedBlob) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      // Give the cropped blob a proper filename so the backend keeps ext.
      const croppedFile = new File([croppedBlob], 'logo.png', { type: 'image/png' });
      fd.append('file', croppedFile);
      fd.append('purpose', 'merchant-logo');
      if (form.business_name) fd.append('subject_name', form.business_name);
      const { data } = await api.post('/api/upload/member-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, business_logo_url: data.url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      setPendingLogoFile(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [me, inds] = await Promise.all([
          api.get('/api/merchant/application/me'),
          api.get('/api/marketplace/industries').catch(() => ({ data: { industries: [] } })),
        ]);
        setApplication(me.data);
        setIndustries(inds.data?.industries || []);
        // Pre-fill rejected re-applications
        if (me.data?.merchant_status === 'rejected' && me.data?.merchant_info) {
          const m = me.data.merchant_info;
          setForm({
            business_name: m.business_name || '',
            business_type: m.business_type || '',
            industry_id: m.industry_id || '',
            business_address: m.business_address || '',
            business_description: m.business_description || '',
            business_logo_url: m.business_logo_url || '',
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.business_address.trim()) {
      toast.error('Business name and address are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/api/merchant/apply', form);
      toast.success('Application submitted! Admins will review it shortly.');
      setApplication({
        merchant_status: 'pending',
        merchant_info: res.data?.user?.merchant_info || {},
        is_merchant_partner: false,
        active_view: res.data?.user?.active_view || 'member',
        role: user?.role,
        subscription_status: user?.subscription_status,
      });
      await refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="p-6 flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  // Gate the page: only members with active subscription should see the form.
  if (user?.role !== 'member') {
    return (
      <MemberSidebarLayout>
        <div className="p-6">
          <Card className="bg-[#041A3B] border-white/10 max-w-xl mx-auto">
            <CardContent className="p-6 text-center space-y-3">
              <AlertCircle className="w-10 h-10 text-[#FFC107] mx-auto" />
              <p className="text-white">Only members can apply for a merchant partnership.</p>
            </CardContent>
          </Card>
        </div>
      </MemberSidebarLayout>
    );
  }

  if (user?.subscription_status !== 'active') {
    return (
      <MemberSidebarLayout>
        <div className="p-6">
          <Card className="bg-[#041A3B] border-white/10 max-w-xl mx-auto" data-testid="apply-requires-subscription">
            <CardContent className="p-6 text-center space-y-4">
              <AlertCircle className="w-10 h-10 text-[#FFC107] mx-auto" />
              <p className="text-white font-medium">Activate a membership tier first</p>
              <p className="text-sm text-[#A0B4CC]">
                You need an active AGA membership before applying as a merchant partner. Subscribe to any tier to unlock the application.
              </p>
              <Button data-testid="apply-go-pricing" onClick={() => navigate('/pricing')} className="bg-[#00E5FF] text-[#001627] hover:bg-[#00B8D4]">
                View Plans
              </Button>
            </CardContent>
          </Card>
        </div>
      </MemberSidebarLayout>
    );
  }

  const status = application?.merchant_status;

  return (
    <MemberSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
            <Store className="w-7 h-7 text-[#00E676]" /> Become a Merchant Partner
          </h1>
          <p className="text-[#A0B4CC] mt-1 text-sm">
            Bring AGA members to your business with an exclusive member discount. After approval, switch between Member and Merchant Mode anytime.
          </p>
        </div>

        {status !== 'approved' && <MerchantPitch />}

        {/* Seamless onboarding — show the user that AGA already has their
            owner info from registration so they only need to fill in the
            BUSINESS details below. Hidden in pending/approved states. */}
        {(status !== 'pending' && status !== 'approved') && (
          <Card className="bg-[#041A3B] border-[#00E676]/20" data-testid="apply-owner-prefill-card">
            <CardContent className="p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#00E676] mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <p className="text-white font-medium">Owner info auto-attached</p>
                <p className="text-[#A0B4CC] mt-0.5">
                  {user?.name}{user?.email ? ` · ${user.email}` : ''}{user?.mobile ? ` · ${user.mobile}` : ''}.
                  Your verified ID and selfie are already on file — no need to re-upload. Just fill in your business details below.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {status === 'pending' && (
          <Card className="bg-[#041A3B] border-[#FFC107]/30" data-testid="application-status-pending">
            <CardContent className="p-5 flex items-start gap-3">
              <Clock className="w-6 h-6 text-[#FFC107] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Application Under Review</p>
                <p className="text-sm text-[#A0B4CC] mt-1">
                  Your application for &ldquo;{application?.merchant_info?.business_name}&rdquo; is queued for admin review. We&apos;ll email you when there&apos;s an update.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {status === 'approved' && (
          <Card className="bg-[#041A3B] border-[#00E676]/30" data-testid="application-status-approved">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#00E676] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-white font-medium">You&apos;re an approved merchant partner!</p>
                <p className="text-sm text-[#A0B4CC] mt-1">
                  Switch to Merchant Mode from the sidebar to manage your services and view discount claims.
                </p>
                <Button
                  className="mt-3 bg-[#00E676] text-[#001627] hover:bg-[#00C853]"
                  data-testid="switch-to-merchant-mode"
                  onClick={async () => {
                    try {
                      await api.post('/api/users/switch-view', { view: 'merchant' });
                      await refreshUser?.();
                      navigate('/merchant');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to switch view');
                    }
                  }}
                >
                  Switch to Merchant Mode
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {status === 'rejected' && (
          <Card className="bg-[#041A3B] border-[#FF6B6B]/30" data-testid="application-status-rejected">
            <CardContent className="p-5 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#FF6B6B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Application not approved</p>
                <p className="text-sm text-[#A0B4CC] mt-1">
                  {application?.merchant_info?.rejection_reason || 'Please update the details and reapply.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {(status !== 'pending' && status !== 'approved') && (
          <Card className="bg-[#041A3B] border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Business Information</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4" data-testid="merchant-apply-form">
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-white">Business Name *</Label>
                  <Input id="business_name" name="business_name" value={form.business_name} onChange={handleChange} required
                    placeholder="e.g., Jolly Eats Cafe"
                    className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                    data-testid="apply-business-name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_type" className="text-white">Business Type</Label>
                    <Input id="business_type" name="business_type" value={form.business_type} onChange={handleChange}
                      placeholder="e.g., Restaurant, Salon, Clinic"
                      className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                      data-testid="apply-business-type" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry_id" className="text-white">Industry</Label>
                    <select id="industry_id" name="industry_id" value={form.industry_id} onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none"
                      data-testid="apply-industry-select">
                      <option value="">Select industry…</option>
                      {industries.map(ind => (
                        <option key={ind.id} value={ind.id}>{ind.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address" className="text-white">Business Address *</Label>
                  <Input id="business_address" name="business_address" value={form.business_address} onChange={handleChange} required
                    placeholder="Street, City, Province, Country"
                    className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                    data-testid="apply-business-address" />
                  <p className="text-[10px] text-[#5C7599]">We&apos;ll geocode this so members nearby can find you in Marketplace.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_description" className="text-white">Business Description</Label>
                  <textarea id="business_description" name="business_description" value={form.business_description} onChange={handleChange}
                    rows={4} placeholder="What does your business offer to AGA members?"
                    className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm placeholder:text-[#5C7599] focus:border-[#00E5FF] focus:outline-none resize-none"
                    data-testid="apply-business-description" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Business Logo (optional)</Label>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="apply-business-logo-file"
                  />
                  {form.business_logo_url ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-md bg-[#000B18] border border-white/10">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-[#041A3B] flex-shrink-0 border border-[#00E5FF]/30">
                        <ImageWithFallback
                          src={form.business_logo_url}
                          alt="Business logo preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0 w-full">
                        <p className="text-[#A0B4CC] text-xs break-all mb-2">{form.business_logo_url}</p>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="ghost" size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="text-[#00E5FF] hover:bg-[#00E5FF]/10"
                            data-testid="apply-business-logo-replace">
                            {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                            Replace
                          </Button>
                          <Button type="button" variant="ghost" size="sm"
                            onClick={() => setForm((f) => ({ ...f, business_logo_url: '' }))}
                            className="text-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                            data-testid="apply-business-logo-remove">
                            <X className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      variant="ghost"
                      className="w-full h-24 border border-dashed border-white/20 text-[#A0B4CC] hover:text-white hover:bg-[#00E5FF]/5 hover:border-[#00E5FF]/40 flex flex-col items-center justify-center gap-1"
                      data-testid="apply-business-logo-upload"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-xs">Uploading…</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span className="text-xs">Tap to upload your logo (PNG/JPG, up to 5&nbsp;MB)</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={submitting} className="bg-[#00E5FF] text-[#001627] hover:bg-[#00B8D4]" data-testid="apply-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {status === 'rejected' ? 'Resubmit Application' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        <CircularImageCropModal
          open={cropOpen}
          onOpenChange={setCropOpen}
          file={pendingLogoFile}
          onConfirm={handleCropConfirm}
          outputSize={512}
          title="Crop your business logo"
        />
      </div>
    </MemberSidebarLayout>
  );
}

export default MerchantApplyPage;
